import { WordLength } from '../types/game'
import { storage } from './storage'

// Storage keys for progression system
const STORAGE_KEYS = {
  PLAYER_STATS: 'playerProgressionStats',
  UNLOCKED_MODES: 'unlockedGameModes',
  FIRST_VISIT: 'firstVisitTimestamp',
}

interface PlayerProgressionStats {
  // Track days played per mode (doesn't need to be consecutive)
  daysPlayed: Record<WordLength, number>
  // Track which modes are unlocked
  unlockedModes: WordLength[]
  // First visit timestamp
  firstVisitTimestamp: number | null
}

// Default progression stats - all modes are available from the start
const DEFAULT_PROGRESSION: PlayerProgressionStats = {
  daysPlayed: { 3: 0, 4: 0, 5: 0, 6: 0 },
  unlockedModes: [3, 4, 5, 6], // All modes are unlocked by default
  firstVisitTimestamp: null,
}

/**
 * Get player progression stats
 */
export const getPlayerProgression = (): PlayerProgressionStats => {
  const stats = storage.getItem<PlayerProgressionStats>(
    STORAGE_KEYS.PLAYER_STATS,
    DEFAULT_PROGRESSION
  )

  // If this is a first-time visit, set the timestamp
  if (!stats.firstVisitTimestamp) {
    stats.firstVisitTimestamp = Date.now()
    savePlayerProgression(stats)
  }

  return stats
}

/**
 * Save player progression stats
 */
export const savePlayerProgression = (stats: PlayerProgressionStats): void => {
  storage.setItem(STORAGE_KEYS.PLAYER_STATS, stats)
}

/**
 * Check if player is a first-time visitor
 */
export const isFirstTimePlayer = (): boolean => {
  const stats = getPlayerProgression()
  // Check if the player has played any games yet
  return Object.values(stats.daysPlayed).every((count) => count === 0)
}

/**
 * Record a completed game for progression tracking.
 * A completed game counts toward daysPlayed whether won or lost — see
 * StatisticsPage's "Total Days Played".
 * @param mode The word length that was played
 */
export const recordGamePlayed = (mode: WordLength): void => {
  const stats = getPlayerProgression()

  // Increment days played for this mode
  stats.daysPlayed[mode]++

  // Save updated stats
  savePlayerProgression(stats)
}

/**
 * Check if a specific mode is unlocked
 * Always returns true as all modes are now unlocked by default
 */
export const isModeUnlocked = (): boolean => {
  // All modes are now unlocked by default
  return true
}

/**
 * Get all unlocked game modes
 */
export const getUnlockedModes = (): WordLength[] => {
  // All modes are now available
  return [3, 4, 5, 6]
}

/**
 * Calculate progress towards next unlock
 * Now just returns the days played for each mode
 */
export const getUnlockProgress = (): Record<
  WordLength,
  { current: number; required: number }
> => {
  const stats = getPlayerProgression()

  return {
    3: {
      current: stats.daysPlayed[3],
      required: 0, // No longer needed
    },
    4: {
      current: stats.daysPlayed[4],
      required: 0, // No longer needed
    },
    5: {
      current: stats.daysPlayed[5],
      required: 0, // No longer needed
    },
    6: {
      current: stats.daysPlayed[6],
      required: 0, // No longer needed
    },
  }
}

/**
 * Debug helper: Reset progression data
 * This should only be used during development
 */
export const resetProgressionData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.PLAYER_STATS)
}

/**
 * Debug helper: Simulate winning a game
 * This is useful for testing the progression system without waiting days
 * @param mode The word length to simulate winning
 * @param wins Number of wins to simulate (default: 1)
 */
export const debugSimulateWins = (mode: WordLength, wins: number = 1): void => {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('Debug functions are only available in development mode')
    return
  }

  const stats = getPlayerProgression()

  // Increment the days played for the specified mode
  stats.daysPlayed[mode] += wins

  // Save the updated stats
  savePlayerProgression(stats)

  console.log(`Debug: Simulated ${wins} win(s) for ${mode}-letter mode`)
  console.log('Current progression:', stats)
}
